import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/ui/Accordion'
import { Container } from '@/components/ui/template/Container'
import { Strong } from '../../../components/ui/Strong'
import { usePepperContractAddress } from '@/hooks/pepper-contract/usePepperContract'
import { cn } from '@/lib/utils'
import { SlideIn } from '../../../components/ui/transitions/SlideIn'
import { useWindowSize } from '@/hooks/helpers/useWindowSize'
import { CopyContainer } from '@/components/CopyContainer'
import { truncateAddress } from '@/utils/truncate-address'
import { testIdFactory } from '@/utils/test-id-factory.ts'

function TextBlock({ children, className }: { children: React.ReactNode; className?: string }) {
	return <p className={cn('text-[1.5rem] text-neutral-40 leading-6', className)}>{children}</p>
}

function Section({ children }: { children: React.ReactNode }) {
	return <section className="flex flex-col gap-5">{children}</section>
}

const faqs = [
	{
		title: 'How was PEPPER distributed?',
		content: (
			<TextBlock>
				PEPPER was distributed via a fair launch to ensure everyone got a fair chance at the spice.{' '}
				<Strong>About 30% of the token supply was airdropped</Strong>, while the rest was up for grabs through
				harvesting!
			</TextBlock>
		)
	},
	{
		title: 'How much PEPPER did the community receive?',
		content: (
			<Section>
				<TextBlock>
					<Strong>Airdrop:</Strong> For every 1 CHZ owned or staked on the governance, the community received
					150,000 PEPPER per airdrop! There were a total of 3 airdrops.
				</TextBlock>
				<TextBlock>
					<Strong>Farming:</Strong> For every 1 CHZ staked, a whopping 250,000 PEPPER could be harvested each
					day.
				</TextBlock>
			</Section>
		)
	},
	{
		title: 'Why can’t I see my PEPPER in my wallet?',
		content: () => {
			const pepperAddress = usePepperContractAddress()

			return (
				<Section>
					<TextBlock>
						If you own PEPPER tokens but they aren't visible in your wallet, it's likely because the token
						hasn't been added yet. To see PEPPER in your wallet, you'll need to manually import the PEPPER
						token by entering the PEPPER smart contract address:
					</TextBlock>
					<CopyContainer
						className="text-[1.5rem] text-neutral-40 leading-6 hover:underline"
						copyable={pepperAddress}
						dataTestId={testIdFactory.FAQCopyAddressContainer()}
					>
						{truncateAddress(pepperAddress)}
					</CopyContainer>
				</Section>
			)
		}
	},
	{
		title: 'What can I do with my PEPPER?',
		content: (
			<TextBlock>
				PEPPER is a fun and playful community token, and its future is yours to shape! While its destiny is in
				the hands of the community, Pepper peoples will be supporting it with some spicy incentives to get
				things cooking. And don’t worry—farmers will keep planting the seeds to help PEPPER grow into something
				even more delicious, PEPPER INC. is our first initiative.
			</TextBlock>
		)
	},
	{
		title: 'What is PEPPER INC.?',
		content: (
			<Section>
				<TextBlock>
					At PEPPER INC.,<Strong>you have the power</Strong> to stake your PEPPER and have a say in what’s
					next for the future of the token through voting! From controlling the PEPPER treasury to deciding on
					what exciting new projects Pepper should grow, <Strong>PEPPER INC.</Strong> is where all the spicy
					decisions are made.
				</TextBlock>
				<TextBlock>
					<Strong>Note:</Strong> Don’t be fooled – it’s not actually an "Inc." but it’s still packed with
					plenty of flavor!
				</TextBlock>
			</Section>
		)
	},
	{
		title: 'What do I need to do to be a part of PEPPER INC.?',
		content: (
			<Section>
				<TextBlock>
					All you need to do is <Strong>own PEPPER</Strong> and stake it in <Strong>PEPPER Inc.</Strong>
					—simple as that.
					<TextBlock>
						Just <Strong>connect your wallet</Strong>, choose how much PEPPER you want to stake, and
						boom—you're part of the decision-making squad.
					</TextBlock>
					<TextBlock>The more you stake, the more your influence!</TextBlock>
				</TextBlock>
			</Section>
		)
	},
	{
		title: 'What are the most common community decisions in PEPPER INC.?',
		content: (
			<Section>
				<TextBlock>
					The most common decision that the PEPPER community will vote on is how to manage the{' '}
					<Strong>PEPPER treasury</Strong>.
				</TextBlock>
				<TextBlock>
					The PEPPER treasury is funded on a regular basis with <Strong>CHZ</Strong> and its mission is
					simple: <Strong>grow the PEPPER community</Strong>. So, the big question the community often faces
					is: <Strong>What should be done with these funds?</Strong> Distribute them to stakers? Buy PEPPER
					and hold or perhaps go crazy and burn the spice?
				</TextBlock>
				<TextBlock>
					Once the voting period has ended and the winning decision is made, you can expect things to start
					rolling within the next <Strong>2-3 days</Strong>. No waiting around – let’s keep it spicy and
					efficient!
				</TextBlock>
			</Section>
		)
	},
	{
		title: 'Can I unstake my PEPPER whenever I want?',
		content: (
			<Section>
				<TextBlock>
					Yes, you can unstake your PEPPER whenever you want – no cooldown period! Simply unstake and then
					claim your PEPPER to have your coins returned to your wallet.
				</TextBlock>
				<TextBlock>
					BUT, here’s the spicy twist: If you’ve voted, your PEPPER gets locked up during the voting period
					and can’t be unstaked until the voting period has ended.
				</TextBlock>
			</Section>
		)
	},
	{
		title: 'Do my PEPPER get locked if I vote through PEPPER INC.?',
		content: (
			<TextBlock>
				Yes, your staked PEPPER is locked during the voting period and cannot be unstaked. However, in the rare
				case that two polls run simultaneously, you can use your locked PEPPER to vote on another poll. The
				locking period will simply update to match the latest voting period.
			</TextBlock>
		)
	},
	{
		title: 'Can I vote multiple times on the same poll?',
		content: (
			<TextBlock>
				You can choose how many PEPPER tokens to use when voting on a poll. However, once your vote is cast and
				your tokens are locked, you won’t be able to change your selection or vote again with a different
				amount.
			</TextBlock>
		)
	},
	{
		title: 'What does “Distribute CHZ to PEPPER stakers” mean?',
		content: (
			<Section>
				<TextBlock>
					If the poll result is “Distribute CHZ to PEPPER stakers”, all CHZ in the PEPPER treasury at the end
					of the voting period will be allocated to users who had PEPPER staked at the exact time of the end
					of the voting period.
				</TextBlock>
				<TextBlock>
					<Strong>How is CHZ distributed?</Strong>
				</TextBlock>
				<TextBlock>
					The amount of CHZ you receive is proportional to how much PEPPER you had staked—more PEPPER staked =
					a larger share of CHZ.
				</TextBlock>
				<TextBlock>
					<Strong>Do I need to vote to receive CHZ?</Strong>
				</TextBlock>
				<TextBlock>
					No! Anyone who staked PEPPER is eligible, whether they voted or not. Voting only determines the
					decision, not the distribution.
				</TextBlock>
			</Section>
		)
	}
] as const

const FAQSection = () => {
	const { isMobile } = useWindowSize()

	return (
		<Container className="bg-green-20" id="faq">
			<Container.Content className="size-full flex flex-col lg:flex-row gap-10 lg:gap-14 justify-center items-center transition-all overflow-hidden">
				<SlideIn from={isMobile ? 'top' : 'bottom'} className="flex-grow-0 lg:self-start lg:mt-4">
					<img
						src="/images/faq.webp"
						alt="Faq image"
						className="size-[160px] md:size-[360px]"
						loading="lazy"
					/>
				</SlideIn>
				<SlideIn from={isMobile ? 'bottom' : 'top'} className="flex flex-col gap-6 flex-1">
					<div className="flex flex-col justify-center items-center lg:items-start gap-3 leading-none">
						<h2 className="text-[5rem] text-secondary font-bold">FAQ</h2>
						<p className="text-[2rem] text-center md:text-start text-neutral-40 font-semibold">
							Everything you need to know about Pepper.
						</p>
					</div>
					<Accordion type="single" collapsible>
						{faqs.map((faq, idx) => {
							return (
								<AccordionItem
									key={idx}
									value={'item-' + idx}
									className="border-neutral-100/10 last:border-b-0 py-1"
								>
									<AccordionTrigger className="text-[1.75rem] font-medium text-start text-neutral-100 uppercase leading-none">
										{faq.title}
									</AccordionTrigger>
									<AccordionContent className="max-w-[300px] md:max-w-[480px] lg:max-w-[612px]">
										{typeof faq.content === 'function' ? faq.content() : faq.content}
									</AccordionContent>
								</AccordionItem>
							)
						})}
					</Accordion>
				</SlideIn>
			</Container.Content>
		</Container>
	)
}

export default FAQSection
